import { ApiResponse, PaginatedApiRequest, PaginatedApiResponse } from './ServiceModels';
import { Device, Sim } from './UserDevicesModels';

export enum UserOperationsType {
  CAN_DELETE = 'CAN_DELETE',
  CAN_EDIT = 'CAN_EDIT',
  CAN_ASSIGN_DEVICE_TO_USER = 'CAN_ASSIGN_DEVICE_TO_USER',
  CAN_ASSIGN_SIM_TO_USER = 'CAN_ASSIGN_SIM_TO_USER',
}

/**
 * User model
 * Used as a model to express a user
 */
export interface User {
  id: string;
  email: string;
  phoneNumber: string;
  billingAccountBan: string;
  firstName: string;
  lastName: string;
  username?: string;
  status: string;
  secondaryEmail?: string;
  creationDate: string;
  updateDate: string;
  devices: number;
  sims: number;
  companyRole?: string;
  department?: string;
  userOperations: UserOperationsType[];
}

/**
 * User model for user creation request
 */
export interface CreateUserRequest {
  firstName: string;
  lastName: string;
  email: string;
  billingAccountBan: string;
  username?: string;
  secondaryEmail?: string;
  companyRole: string;
  department: string;
}

/**
 * Create user response
 * API POST /users/create
 */
export type CreateUserResponse = ApiResponse & User;

/**
 * User model for user creation request
 */
export interface UpdateUserRequest {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  companyRole: string;
}

/**
 * Update user response
 * API POST /users/update
 */
export type UpdateUserResponse = ApiResponse & User;

/**
 * Request model to verify if the user already exists
 * API POST /users/exists
 */
export interface UserExistsRequest {
  email: string;
  username: string;
}

/**
 * Response model when received when verifying the if a user exists
 * API POST /users/exists
 */
export type UserExistsUser = Omit<User, 'devices' | 'sims' | 'phoneNumber' | 'creationDate' | 'updateDate'>;
export interface UserExistsResponse extends ApiResponse {
  exists: boolean;
  user: UserExistsUser;
}

/**
 * User response
 * Used for single user request
 */
export type UserResponse = User;

/**
 * Paginated users list
 * Response from API
 */
export interface UsersListResponse extends PaginatedApiResponse {
  users: User[];
}

/**
 * Users list request
 * Request params to get the list of users
 */
export interface UsersListRequest extends PaginatedApiRequest {
  filter: string;
}

/**
 * Single user request
 */
export interface UserRequest {
  id: string;
}

/**
 * Request of a device by eid for a given user
 */
export interface DeviceByEidRequest {
  eid: string;
  userId: string;
  upgrade: boolean;
  userBan?: string;
}

/**
 * Response that returns a Device by eid for a given user
 * Response from API POST /devices/eid
 */
export interface DeviceByEidResponse extends ApiResponse {
  device: Device;
  sim: Sim;
  user?: User;
}

/**
 * Response of the scheduler contains user request
 * GET /scheduler/contains-user/:id
 */
export interface SchedulerContainsUserResponse extends ApiResponse {
  userId: string;
  hasSchedulers: boolean;
}

export type DeleteUserResponse = ApiResponse;

export interface UserOperationsResponse extends ApiResponse {
  userOperations: UserOperationsType[];
}

/**
 * Request of a device by number for a given user
 */
export interface DeviceByNumberRequest {
  number: string;
}
